import React, { useState, useEffect, useRef, useCallback } from 'react';
import BaseModal from '../BaseModal';
import Icon from '@mdi/react';
import { mdiTicketPercent, mdiContentCopy } from '@mdi/js';
import { toast } from 'react-toastify';
import { classApi } from '../../api/classes';
import { formatCurrencyFromMinor } from '../../utils/currency';
import './TrialDealEditModal.css';

const SLUG_RE = /^[a-z0-9-]+$/;

const TrialDealEditModal = ({ isOpen, onClose, onSaved, trialDeal, allTerms = [], allClasses = [] }) => {
    const isEdit = !!trialDeal;

    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [durationDays, setDurationDays] = useState('7');
    const [startDate, setStartDate] = useState('');
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [ctaLabel, setCtaLabel] = useState('Buy Deal Now');
    const [selectedTermId, setSelectedTermId] = useState('');
    const [selectedClassIds, setSelectedClassIds] = useState(new Set());

    const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
    const [isSaving, setIsSaving] = useState(false);

    const slugTimerRef = useRef(null);

    // Populate form when editing
    useEffect(() => {
        if (!isOpen) return;
        if (trialDeal) {
            setName(trialDeal.name || '');
            setPrice(trialDeal.price != null ? String(trialDeal.price / 100) : ''); // cents → dollars for the input
            setDurationDays(trialDeal.durationDays != null ? String(trialDeal.durationDays) : '7');
            setStartDate(trialDeal.startDate ? trialDeal.startDate.substring(0, 10) : '');
            setSlug(trialDeal.slug || '');
            setDescription(trialDeal.description || trialDeal.landingContent?.bodyHtml || '');
            setCtaLabel(trialDeal.landingContent?.ctaLabel || 'Buy Deal Now');
            setSelectedTermId(trialDeal.termId || '');
            setSelectedClassIds(new Set(trialDeal.eligibleClassIds || []));
        } else {
            setName('');
            setPrice('');
            setDurationDays('7');
            setStartDate(new Date().toISOString().substring(0, 10));
            setSlug('');
            setDescription('');
            setCtaLabel('Buy Deal Now');
            setSelectedTermId(allTerms.length === 1 ? allTerms[0].id : '');
            setSelectedClassIds(new Set());
        }
        setSlugStatus(null);
    }, [isOpen, trialDeal, allTerms]);

    const autoSlug = useCallback((value) => {
        return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }, []);

    const checkSlug = useCallback(async (value, excludeId) => {
        if (!value || !SLUG_RE.test(value)) {
            setSlugStatus(value ? 'invalid' : null);
            return;
        }
        setSlugStatus('checking');
        try {
            const r = await classApi.checkTrialSlug(value, excludeId);
            setSlugStatus(r.ok && r.data.available ? 'available' : 'taken');
        } catch {
            setSlugStatus(null);
        }
    }, []);

    const handleSlugChange = (value) => {
        const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setSlug(cleaned);
        setSlugStatus(null);
        clearTimeout(slugTimerRef.current);
        slugTimerRef.current = setTimeout(() => checkSlug(cleaned, trialDeal?.id), 600);
    };

    const handleNameBlur = () => {
        if (!slug && name) {
            const generated = autoSlug(name);
            setSlug(generated);
            checkSlug(generated, trialDeal?.id);
        }
    };

    const termClasses = selectedTermId
        ? allClasses.filter(c => c.termId === selectedTermId).sort((a, b) => a.name.localeCompare(b.name))
        : [];

    const handleTermChange = (termId) => {
        setSelectedTermId(termId);
        setSelectedClassIds(new Set());
    };

    const toggleClass = (id) => {
        setSelectedClassIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSave = async () => {
        if (!name.trim()) { toast.error('Name is required'); return; }
        if (price === '' || isNaN(Number(price)) || Number(price) < 0) { toast.error('Valid price is required'); return; }
        if (!selectedTermId) { toast.error('Please select a term for this trial deal'); return; }
        if (!slug || !SLUG_RE.test(slug)) { toast.error('Custom URL is required (lowercase letters, numbers, hyphens only)'); return; }
        if (slugStatus === 'taken') { toast.error('That custom URL is already taken'); return; }

        setIsSaving(true);
        try {
            const payload = {
                name: name.trim(),
                slug,
                price: Math.round(Number(price) * 100), // dollars input → integer cents
                durationDays: Number(durationDays) || 7,
                termId: selectedTermId || null,
                eligibleClassIds: [...selectedClassIds],
                ...(startDate && { startDate }),
                description: description.trim(),
                landingContent: {
                    ctaLabel: ctaLabel.trim() || 'Buy Deal Now',
                    bodyHtml: description.trim(),
                },
            };
            const r = isEdit
                ? await classApi.updateTrialDeal(trialDeal.id, payload)
                : await classApi.createTrialDeal(payload);
            if (r.ok) {
                toast.success(isEdit ? 'Trial deal updated' : 'Trial deal created');
                onSaved(r.data.trialDeal);
            } else {
                toast.error(r.data?.message || 'Failed to save trial deal');
            }
        } catch {
            toast.error('Failed to save trial deal');
        } finally {
            setIsSaving(false);
        }
    };

    const slugStatusNode = slugStatus === 'checking' ? (
        <span className="tdm-slug-hint tdm-slug-hint--checking">Checking...</span>
    ) : slugStatus === 'available' ? (
        <span className="tdm-slug-hint tdm-slug-hint--ok">Available</span>
    ) : slugStatus === 'taken' ? (
        <span className="tdm-slug-hint tdm-slug-hint--taken">Already taken</span>
    ) : slugStatus === 'invalid' ? (
        <span className="tdm-slug-hint tdm-slug-hint--taken">Lowercase letters, numbers and hyphens only</span>
    ) : null;

    const copyLink = () => {
        const url = `${window.location.origin}/trial/${slug}`;
        navigator.clipboard.writeText(url)
            .then(() => toast.success('Link copied!'))
            .catch(() => toast.error('Could not copy link. Please copy it manually.'));
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Trial Deal' : 'New Trial Deal'}
            icon={mdiTicketPercent}
            maxWidth={720}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="base-modal-save-btn"
                        onClick={handleSave}
                        disabled={isSaving || slugStatus === 'taken' || slugStatus === 'checking'}>
                        {isSaving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Deal')}
                    </button>
                </>
            }>
            <div className="tdm-form">
                {isEdit && (
                    <div className="tdm-stats-block">
                        <div className="tdm-stats-row">
                            <span className="tdm-stats-label">Sold to date</span>
                            <span className="tdm-stats-value">{trialDeal?.purchaseCount || 0}</span>
                        </div>
                        <div className="tdm-stats-row">
                            <span className="tdm-stats-label">Revenue</span>
                            <span className="tdm-stats-value">
                                {formatCurrencyFromMinor(trialDeal?.totalRevenue, trialDeal?.currency)}
                            </span>
                        </div>
                    </div>
                )}

                <label className="tdm-label">Name *</label>
                <input
                    className="tdm-input"
                    placeholder="e.g. Try Latin Dance, May 2026"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onBlur={handleNameBlur}
                />

                <label className="tdm-label">Price ($) *</label>
                <input
                    className="tdm-input tdm-input--short"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="20"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                />

                <div className="tdm-row">
                    <div className="tdm-col">
                        <label className="tdm-label">Start date</label>
                        <input
                            className="tdm-input"
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="tdm-col">
                        <label className="tdm-label">Duration (days)</label>
                        <p className="tdm-hint">Deal expires after this many days</p>
                        <input
                            className="tdm-input"
                            type="number"
                            min="1"
                            placeholder="7"
                            value={durationDays}
                            onChange={e => setDurationDays(e.target.value)}
                        />
                    </div>
                </div>

                <label className="tdm-label">Custom URL *</label>
                <p className="tdm-hint">Lowercase letters, numbers, and hyphens only. Auto-generated from name if left blank.</p>
                <input
                    className="tdm-input"
                    placeholder="e.g. try-latin-dance-may-2026"
                    value={slug}
                    onChange={e => handleSlugChange(e.target.value)}
                />
                {slugStatusNode}
                {slug && (
                    <div className="tdm-url-preview">
                        <span className="tdm-url-preview-label">Your link</span>
                        <span className="tdm-url-preview-value">{window.location.origin}/trial/{slug}</span>
                        <button
                            type="button"
                            className="trial-deal-url-panel__copy-btn"
                            onClick={copyLink}
                            title="Copy link">
                            <Icon path={mdiContentCopy} size={0.75} />
                        </button>
                    </div>
                )}

                <label className="tdm-label">Term *</label>
                <p className="tdm-hint">The trial deal is tied to a specific term. Only classes from that term can be selected.</p>
                {allTerms.length === 0 ? (
                    <p className="tdm-empty">No terms are currently open for enrolment. Go to Terms and open a term for enrolment before creating a trial deal.</p>
                ) : (
                    <select
                        className="tdm-input"
                        value={selectedTermId}
                        onChange={e => handleTermChange(e.target.value)}>
                        <option value="">Select a term...</option>
                        {allTerms.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                )}

                {selectedTermId && (
                    <>
                        <label className="tdm-label">Eligible classes</label>
                        <p className="tdm-hint">Which classes from this term can be trialled? Leave all unchecked to allow any class in the term.</p>
                        {termClasses.length === 0 ? (
                            <p className="tdm-empty">No classes found in this term.</p>
                        ) : (
                            <div className="tdm-class-list">
                                {termClasses.map(cls => (
                                    <label key={cls.id} className="tdm-class-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedClassIds.has(cls.id)}
                                            onChange={() => toggleClass(cls.id)}
                                        />
                                        <span className="tdm-class-name">{cls.name}</span>
                                        <span className="tdm-class-meta">
                                            {cls.style}
                                            {cls.dayOfWeek != null ? ` · ${'Sun,Mon,Tue,Wed,Thu,Fri,Sat'.split(',')[cls.dayOfWeek]}` : ''}
                                            {cls.startTime ? ` ${cls.startTime}` : ''}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <label className="tdm-label">Description</label>
                <p className="tdm-hint">This text appears on your trial deal page. Tell students what they get, what to expect, and why to sign up.</p>
                <textarea
                    className="tdm-textarea"
                    rows={7}
                    placeholder="Tell prospective students what they get with this trial deal..."
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />

                <label className="tdm-label">CTA button label</label>
                <input
                    className="tdm-input"
                    placeholder="Buy Deal Now"
                    value={ctaLabel}
                    onChange={e => setCtaLabel(e.target.value)}
                />

            </div>
        </BaseModal>
    );
};

export default TrialDealEditModal;
